/**
 * <s-cart-terms> — Terms of Service agreement required before checkout (cart drawer + cart page).
 *
 * - Unchecked on every page load; the choice lives only for this page (drawer re-renders keep it).
 * - A window-level capture listener runs before any app handler and blocks, until agreed:
 *   theme checkout buttons ([name="checkout"]), Navidium's checkout controls (any button or link inside its
 *   widget whose text mentions checkout, e.g. "Protected Checkout" / "Checkout without protection"),
 *   and cart form submissions made with a checkout submitter.
 * - Cannot gate: Buy Now / accelerated checkout buttons (Shopify-rendered, they open checkout themselves)
 *   or a shopper typing /checkout. See the purchase-testing report.
 */
let agreed = false;

const all = () => [...document.querySelectorAll('s-cart-terms')];

const navidiumScope = '.nvd-mini, #nvd-cart, [class*="nvd-widget"], [id^="nvd-"], [class^="nvd-"]';

function checkoutControl(target) {
  if (!(target instanceof Element)) return null;
  const themeButton = target.closest('[name="checkout"], #dyn-checkout, #nvd-with-checkout-btn, #nvd-without-checkout-btn');
  if (themeButton) return themeButton;
  const control = target.closest('button, a, [role="button"], input[type="submit"]');
  if (control && control.closest(navidiumScope) && /checkout/i.test(control.textContent || control.value || '')) return control;
  return null;
}

function nearestTerms(from) {
  const scope = from?.closest('dialog, [data-cart-scope], main') || document;
  return scope.querySelector('s-cart-terms') || all()[0] || null;
}

function block(event, from) {
  event.preventDefault();
  event.stopImmediatePropagation();
  const terms = nearestTerms(from);
  if (!terms) return;
  terms.showError();
}

window.addEventListener('click', (event) => {
  if (agreed || !all().length) return;
  const control = checkoutControl(event.target);
  if (control) block(event, control);
}, true);

window.addEventListener('submit', (event) => {
  if (agreed || !all().length) return;
  const submitter = event.submitter;
  if (submitter && submitter.getAttribute('name') === 'checkout') block(event, submitter);
}, true);

class CartTerms extends HTMLElement {
  connectedCallback() {
    const box = this.#box;
    if (!box) return;
    box.checked = agreed;
    box.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.#box?.removeEventListener('change', this.#onChange);
  }

  get #box() {
    return this.querySelector('[data-cart-terms-checkbox]');
  }

  #onChange = (event) => {
    agreed = event.currentTarget.checked;
    all().forEach((terms) => terms.sync());
  };

  sync() {
    const box = this.#box;
    if (box) box.checked = agreed;
    if (agreed) this.#error(false);
  }

  showError() {
    this.#error(true);
    this.#box?.focus();
  }

  #error(show) {
    const error = this.querySelector('[data-cart-terms-error]');
    const box = this.#box;
    if (!error || !box) return;
    error.hidden = !show;
    // role="alert" only while shown, so screen readers announce it each time checkout is blocked.
    if (show) { error.setAttribute('role', 'alert'); box.setAttribute('aria-invalid', 'true'); }
    else { error.removeAttribute('role'); box.removeAttribute('aria-invalid'); }
  }
}

if (!customElements.get('s-cart-terms')) customElements.define('s-cart-terms', CartTerms);
